import React, { Component } from "react";
import { Translation } from "react-i18next";
import CalculatedPayment from "src/containers/CalculatedPayment";
import styled from "styled-components";
import { analyticsFEA } from "src/AnalyticsFEA";
import DealerOfferPayment from "src/components/DealerOfferPayment";
import ContinueDealerOfferButton from "src/components/MyGarage/VehicleHeader/ContinueDealerOfferButton";
import VehicleHeaderThumbnail from "src/components/MyGarage/VehicleHeader/VehicleHeaderThumbnail";
import VinNumber from "src/components/VinNumber/VinNumber";
import { isEmpty } from "src/utils/object";
import { ApiVehicle } from "Types/api/Vehicle";
import { AccountState, VehicleState } from "Types/state";
import Remove from "../../icons/Remove";
import Rating from "../Rating";
import { getCurrentOffer, getPaymentType } from "../TableCompare/lib";
import AddVehicle from "./AddVehicle";
import ExploreMore from "./ExploreMore";
import "./VehicleHeader.scss";

const VinNumberCustom = styled(VinNumber)`
    font-size: 10px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        font-size: 13px;
    }
`;

const DealerAddedTextContainer = styled("div")`
    text-align: center;
    margin-top: 5px;
    color: ${(props) => props.theme.palette.primary.dark};
    font-size: 0.875em;
    font-weight: bold;
    letter-spacing: normal;
`;

export interface VehicleHeaderProps {
    account: AccountState;
    dealId?: string;
    dealState?: string;
    isPaymentsLoading: boolean;
    hideRemove?: boolean;
    minHeight?: number;
    srpLink?: string;
    vehicle?: VehicleState;
    isDealerAddedVehicle: boolean;
    onUpdate?: () => void;
    removeVehicle?: (vin: string) => any;
    toggleOSModal?: () => void;
    updateVehicleRating: (uuid: string, vin: string, rating: number, prevRating?: number) => void;
}

class VehicleHeader extends Component<VehicleHeaderProps> {
    public static defaultProps: Partial<VehicleHeaderProps> = {
        hideRemove: false,
    };

    public componentDidMount() {
        this.handleUpdate();
    }

    public componentWillUnmount() {
        this.handleUpdate();
    }

    public render() {
        const { vehicle, dealState, hideRemove, minHeight, isDealerAddedVehicle } = this.props;

        if (!vehicle || !vehicle.vin || (vehicle && vehicle.loading)) {
            return (
                <AddVehicle
                    loading={vehicle && !!vehicle.loading}
                    srpLink={this.props.srpLink}
                    minHeight={minHeight}
                    toggleOSModal={this.props.toggleOSModal}
                />
            );
        }

        const isDisabled = dealState && dealState.length > 0;
        const isDealerOfferVehicle = !!vehicle.payments?.dealer;
        const vehicleDeal = {
            selectedPaymentIndex: getPaymentType(vehicle),
            tip: "",
            vin: vehicle.vin,
        } as any;
        const isDealVehicleInStock = !!vehicle.gone;

        return (
            <div
                data-e2e="vehicle-header-container"
                className={`VehicleHeader`}
                style={minHeight ? { height: minHeight } : {}}
            >
                <div className="VehicleHeader__imageWrapper">
                    {!hideRemove && !isDealerOfferVehicle && (
                        <div
                            data-e2e="remove-vehicle-btn"
                            className="VehicleHeader__delete--container"
                            onClick={this.remove}
                        >
                            <Remove className="VehicleHeader__delete" />
                        </div>
                    )}
                    {isDisabled && (
                        <div className="VehicleHeader__dealStatus">
                            <p>{dealState}</p>
                        </div>
                    )}
                    {vehicle.isDeleting && (
                        <div className="VehicleHeader__dealStatus">
                            <Translation>
                                {(t) => <p>{t("MyGarage.TableCompare.VehicleHeader.deleting")}</p>}
                            </Translation>
                        </div>
                    )}
                    <VehicleHeaderThumbnail
                        vin={vehicle.vin}
                        onLoad={this.handleUpdate}
                        isDealerVehicle={isDealerOfferVehicle}
                        isVehicleGone={isDealVehicleInStock}
                    />
                </div>
                <p className="VehicleHeader__info" data-e2e="vehicleHeaderInfo">
                    <span className="VehicleHeader__info-line2" title={`${vehicle.title}`}>
                        {vehicle.title}
                    </span>
                    <VinNumberCustom>{vehicle.vin}</VinNumberCustom>
                </p>
                <div
                    className="VehicleHeader__priceContainer"
                    style={{
                        justifyContent: this.props.isPaymentsLoading ? "space-around" : "flex-start",
                    }}
                >
                    {isDealerOfferVehicle ? (
                        <DealerOfferPayment vin={vehicle.vin} />
                    ) : (
                        <CalculatedPayment {...vehicleDeal} inGarage={true} />
                    )}
                </div>
                {"dealer" in (vehicle.payments ?? {}) ? (
                    <ContinueDealerOfferButton vin={vehicle.vin} />
                ) : (
                    <ExploreMore
                        continueDealAllowed={this.canContinueDeal(vehicle)}
                        vehicle={vehicle}
                        vin={vehicle.vin}
                        key={`exploreMore${vehicle.vin}`}
                    />
                )}
                <div className="VehicleHeader__ratingContainer">
                    {!isDealerOfferVehicle &&
                        (isDealerAddedVehicle ? (
                            <DealerAddedTextContainer>
                                <Translation>
                                    {(t) => <span>{t(`MyGarage.TableCompare.VehicleHeader.dealerAddedText`)}</span>}
                                </Translation>
                            </DealerAddedTextContainer>
                        ) : (
                            <Rating
                                id={vehicle.vin}
                                onChange={this.sort}
                                rating={vehicle.rating || 0}
                                key={`starRating${vehicle.vin}`}
                            />
                        ))}
                </div>
            </div>
        );
    }

    protected handleUpdate = () => {
        const { onUpdate } = this.props;
        if (onUpdate) onUpdate();
    };

    protected canContinueDeal = (vehicle: ApiVehicle): boolean => {
        return !isEmpty(getCurrentOffer(vehicle));
    };

    protected remove = () => {
        if (this.props.vehicle) {
            this.props.removeVehicle && this.props.removeVehicle(this.props.vehicle.vin);
            if (this.props.dealId) {
                analyticsFEA.engine.dropSave(this.props.dealId, [this.props.vehicle]);
            }
        }
    };

    protected sort = async (vin: any, rating: any) => {
        this.props.updateVehicleRating(this.props.dealId!, vin, rating, this.props.vehicle?.rating);
    };
}

export default VehicleHeader;
